import React, { Fragment, useContext, useState, useRef } from 'react';
import Drawer from '@src/components/wysiwyg_editor/toolbox/Drawer';
import useAddText from '@src/hooks/stencil/useAddText';
import plus from '@images/wysiwyg_editor/add_button_plus.svg';
import useOnClickOutside from 'use-onclickoutside';
import ColorPicker from '@src/components/wysiwyg_editor/toolbox/ColorPicker';
import { TextEditorDispatchContext, TextEditorStateContext } from '@src/contexts/wysiwyg/text_editor';
import _uniq from 'lodash/uniq';
import Header from '@src/components/wysiwyg_editor/toolbox/drawer_headers/Text';
import useIsLargeScreen from '../../../../hooks/useIsLargeScreen';
import { isIE, isIOS, isMobile } from 'react-device-detect';
import { WysiwygEditorDispatchContext } from '@src/contexts/wysiwyg/editor';
import { TextCacheDispatchContext, TextCacheStateContext } from '@src/contexts/wysiwyg/text_cache';
import { $fontStackPrimary } from '@src/variables';
import { isNode } from '@src/utils/typescript';
import useSetTextBackgroundColor from '@src/hooks/stencil/useSetTextBackgroundColor';
import useSelectedTextBoxBackgroundColor from '@src/hooks/stencil/useSelectedTextBoxBackgroundColor';

const FONT_SIZES = [10, 11, 12, 13, 14, 15, 16, 18, 20, 22, 24, 28, 32, 36, 42, 50, 60, 72, 96, 128];

function Text(): React.ReactElement {
  const isLarge = useIsLargeScreen();
  const addText = useAddText();
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const [showBackgroundColorPicker, setShowBackgroundColorPicker] = useState(false);
  const backgroundColorPickerRef = useRef<HTMLDivElement | null>(null);
  const textColorPickerRef = useRef<HTMLDivElement | null>(null);
  const backgroundColorPickerTriggerRef = useRef<HTMLButtonElement | null>(null);
  const textColorPickerTriggerRef = useRef<HTMLButtonElement | null>(null);
  const {
    activeView,
    textEditorContainerFocused,
    isBold,
    isItalic,
    selectedAlignment,
    selectedColor,
    selectedFont,
    selectedSize
  } = useContext(TextEditorStateContext);
  const { applyAlignment, applyColor, applyFont, applySize, focusEditor, toggleBold, toggleItalic } =
    useContext(TextEditorDispatchContext);
  const { closeToolbox } = useContext(WysiwygEditorDispatchContext);
  const { texts } = useContext(TextCacheStateContext);
  const { startDragging } = useContext(TextCacheDispatchContext);
  const setBackgroundColor = useSetTextBackgroundColor();
  const selectedBackgroundColor = useSelectedTextBoxBackgroundColor();

  // a size of two is just the opening and closing nodes
  const activeTextEditorHasContent = activeView?.state?.doc?.content?.size > 2;

  const enableControls =
    (activeView && !textEditorContainerFocused) || (textEditorContainerFocused && activeTextEditorHasContent);

  useOnClickOutside(backgroundColorPickerRef, (e: MouseEvent) => {
    if (!isNode(e.target)) return;

    const triggerClicked = e.target === backgroundColorPickerTriggerRef.current;
    const triggerContainsClickedElement = backgroundColorPickerTriggerRef.current?.contains(e.target);

    if (!triggerClicked && !triggerContainsClickedElement) {
      setShowBackgroundColorPicker(false);
    }
  });

  useOnClickOutside(textColorPickerRef, (e: MouseEvent) => {
    if (!isNode(e.target)) return;

    const triggerClicked = e.target === textColorPickerTriggerRef.current;
    const triggerContainsClickedElement = textColorPickerTriggerRef.current?.contains(e.target);

    if (!triggerClicked && !triggerContainsClickedElement) {
      setShowTextColorPicker(false);
    }
  });

  const toggleBackgroundColorPicker = () => {
    if (showBackgroundColorPicker) {
      setShowBackgroundColorPicker(false);

      // applying to entire content means the text editor hasn't been focused
      // to start with
      if (!textEditorContainerFocused) focusEditor();
    } else {
      setShowBackgroundColorPicker(true);
    }
  };

  const toggleTextColorPicker = () => {
    if (showTextColorPicker) {
      setShowTextColorPicker(false);

      // applying to entire content means the text editor hasn't been focused
      // to start with
      if (!textEditorContainerFocused) focusEditor();
    } else {
      setShowTextColorPicker(true);
    }
  };

  return (
    <Drawer className="wysiwyg-editor-toolbox-drawer-text" header={Header}>
      <button
        type="button"
        className="wysiwyg-editor-toolbox-drawer-text-addtext"
        onClick={() => {
          addText();
          if (!isLarge) closeToolbox();
        }}
      >
        <img src={plus as string} alt="" /> Add Text
      </button>
      {isIOS || isMobile ? (
        <div className="wysiwyg-editor-toolbox-control-group">
          <p>
            If you would like to customize the styling of your text (colors/bold/etc), visit this page on a laptop or
            desktop browser.
          </p>
        </div>
      ) : (
        <Fragment>
          <div className="wysiwyg-editor-toolbox-control-group">
            <select
              className="wysiwyg-editor-toolbox-select"
              value={selectedFont}
              disabled={!enableControls}
              onChange={(e: React.FormEvent<HTMLSelectElement>) => applyFont((e.target as HTMLSelectElement).value)}
            >
              <option value="eb-garamond">EB Garamond</option>
              <option value="open-sans">Open Sans</option>
              <option value="playfair-display">Playfair Display</option>
              <option value="poppins">Poppins</option>
              <option value="quicksand">Quicksand</option>
              <option value="roboto">Roboto</option>
            </select>
          </div>

          <div className="wysiwyg-editor-toolbox-control-groups--inline">
            <div className="wysiwyg-editor-toolbox-control-group">
              <select
                className="wysiwyg-editor-toolbox-select"
                value={selectedSize}
                disabled={!enableControls}
                onChange={(e: React.FormEvent<HTMLSelectElement>) => {
                  applySize(Number((e.target as HTMLSelectElement).value));
                }}
              >
                {_uniq<number>(FONT_SIZES.concat([selectedSize]).sort((a: number, b: number) => a - b)).map((size) => (
                  <option key={`font-size=${size}`} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            <div className="wysiwyg-editor-toolbox-control-group">
              <button
                className="wysiwyg-editor-toolbox-button-color"
                ref={textColorPickerTriggerRef}
                type="button"
                disabled={!enableControls}
                onClick={toggleTextColorPicker}
              >
                <span className="wysiwyg-editor-toolbox-button-color-preview" style={{ background: selectedColor }} />
                <span className="wysiwyg-editor-toolbox-control-label" style={{ marginLeft: '0.5rem' }}>
                  Text
                </span>
              </button>
              {showTextColorPicker ? (
                <ColorPicker
                  ref={textColorPickerRef}
                  triggerRef={textColorPickerTriggerRef}
                  selectedColor={selectedColor}
                  onChange={applyColor}
                />
              ) : null}
            </div>
          </div>

          <div className="wysiwyg-editor-toolbox-control-group">
            <button
              className="wysiwyg-editor-toolbox-button-color"
              ref={backgroundColorPickerTriggerRef}
              type="button"
              disabled={!enableControls}
              onClick={toggleBackgroundColorPicker}
            >
              <span
                className="wysiwyg-editor-toolbox-button-color-preview"
                style={{ background: selectedBackgroundColor }}
              />
              <span className="wysiwyg-editor-toolbox-control-label" style={{ marginLeft: '0.5rem' }}>
                Background
              </span>
            </button>
            {showBackgroundColorPicker ? (
              <ColorPicker
                ref={backgroundColorPickerRef}
                triggerRef={backgroundColorPickerTriggerRef}
                selectedColor={selectedBackgroundColor}
                onChange={setBackgroundColor}
              />
            ) : null}
          </div>

          <div className="wysiwyg-editor-toolbox-control-group">
            <div className="wysiwyg-editor-toolbox-button-group">
              <button
                type="button"
                disabled={!enableControls}
                data-selected={isBold}
                onClick={(_e: React.MouseEvent<HTMLButtonElement>) => toggleBold(!isBold)}
              >
                <i className="fa fa-bold" />
              </button>
              <button
                type="button"
                disabled={!enableControls}
                data-selected={isItalic}
                onClick={(_e: React.MouseEvent<HTMLButtonElement>) => toggleItalic(!isItalic)}
              >
                <i className="fa fa-italic" />
              </button>
            </div>
          </div>

          <div className="wysiwyg-editor-toolbox-control-group">
            <div className="wysiwyg-editor-toolbox-button-group">
              <button
                type="button"
                disabled={!enableControls}
                data-selected={selectedAlignment === 'left'}
                onClick={(_e: React.MouseEvent<HTMLButtonElement>) => applyAlignment('left')}
              >
                <i className="fa fa-align-left" />
              </button>
              <button
                type="button"
                disabled={!enableControls}
                data-selected={selectedAlignment === 'right'}
                onClick={(_e: React.MouseEvent<HTMLButtonElement>) => applyAlignment('right')}
              >
                <i className="fa fa-align-right" />
              </button>
              <button
                type="button"
                disabled={!enableControls}
                data-selected={selectedAlignment === 'center'}
                onClick={(_e: React.MouseEvent<HTMLButtonElement>) => applyAlignment('center')}
              >
                <i className="fa fa-align-center" />
              </button>
            </div>
          </div>

          {isLarge && texts.length ? (
            <section className="wysiwyg-editor-drawer-textcache">
              <h4 style={{ fontFamily: $fontStackPrimary, marginTop: '3rem' }}>Text Drafts</h4>

              {texts.map((item, idx) => (
                <div
                  className="wysiwyg-editor-drawer-textcache-item"
                  key={idx}
                  onDragStart={(e: React.DragEvent<HTMLDivElement>) => {
                    const textRect = (e.target as HTMLDivElement).getBoundingClientRect();

                    const dragX = (e.clientX - textRect.left) / textRect.width;
                    const dragY = (e.clientY - textRect.top) / textRect.height;

                    // We originally used 'application/json' but Internet
                    // Explorer only allows using the key "Text".
                    e.dataTransfer.setData(
                      'Text',
                      JSON.stringify({
                        data: item,
                        dragOffset: { x: dragX, y: dragY }
                      })
                    );

                    if (!isIE) e.dataTransfer.dropEffect = 'copy';

                    setTimeout(() => startDragging(), 0);
                  }}
                  draggable
                >
                  {item.textContent}
                </div>
              ))}
            </section>
          ) : null}
        </Fragment>
      )}
    </Drawer>
  );
}

export default Text;
